import { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { 
  Inbox as InboxIcon, CheckCircle2, MessageSquare, 
  AtSign, Calendar, Loader2, Bell, Archive
} from 'lucide-react';
import { toast } from 'sonner';

const Inbox = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInboxData = useCallback(async () => {
    try {
      // Fetch tasks assigned to current user
      const tasksRes = await api.get('/tasks', { params: { assigned_to: user?.user_id } });
      setAssignedTasks(tasksRes.data || []);
      
      // In a full implementation, we'd also fetch notifications and mentions
      // For now, we'll show assigned tasks as the main inbox content
    } catch (error) {
      console.error('Failed to fetch inbox:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchInboxData();
  }, [fetchInboxData]);

  const handleTaskComplete = async (taskId) => {
    try {
      await api.post(`/tasks/${taskId}/complete`);
      setAssignedTasks(prev => prev.map(t => 
        t.task_id === taskId ? { ...t, completed: !t.completed } : t
      ));
      toast.success('Task updated');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 text-[#4A9CC7] animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="h-full flex flex-col" data-testid="inbox-page">
        {/* Header */}
        <div className="flex-shrink-0 border-b bg-white px-6 py-4">
          <h1 className="text-xl font-bold text-gray-900">Inbox</h1>
          <p className="text-sm text-gray-500">Tasks assigned to you and notifications</p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <Tabs defaultValue="assigned" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="assigned" className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Assigned to Me
                {assignedTasks.filter(t => !t.completed).length > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {assignedTasks.filter(t => !t.completed).length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="mentions" className="flex items-center gap-2">
                <AtSign className="w-4 h-4" />
                Mentions
              </TabsTrigger>
              <TabsTrigger value="notifications" className="flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Notifications
              </TabsTrigger>
            </TabsList>

            <TabsContent value="assigned">
              <div className="space-y-2">
                {assignedTasks.filter(t => !t.completed).length === 0 ? (
                  <div className="text-center py-12">
                    <InboxIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <h3 className="font-medium text-gray-700">All caught up!</h3>
                    <p className="text-sm text-gray-500 mt-1">No tasks assigned to you</p>
                  </div>
                ) : (
                  assignedTasks.filter(t => !t.completed).map(task => (
                    <Card key={task.task_id} className="hover:shadow-sm transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <button
                            onClick={() => handleTaskComplete(task.task_id)}
                            className="mt-0.5 text-gray-400 hover:text-[#4A9CC7]"
                          >
                            <CheckCircle2 className="w-5 h-5" />
                          </button>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{task.title}</h4>
                            {task.description && (
                              <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                {task.description}
                              </p>
                            )}
                            <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                              {task.due_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {new Date(task.due_date).toLocaleDateString()}
                                </span>
                              )}
                              {task.workspace_name && (
                                <span>{task.workspace_name}</span>
                              )}
                              {task.project_name && (
                                <Badge variant="outline" className="text-[10px]">
                                  {task.project_name}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Badge 
                            className={`text-xs ${
                              task.priority === 'high' ? 'bg-red-100 text-red-700' :
                              task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}
                          >
                            {task.priority}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}

                {/* Completed Tasks */}
                {assignedTasks.filter(t => t.completed).length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                      <Archive className="w-4 h-4" />
                      Completed
                    </h3>
                    {assignedTasks.filter(t => t.completed).slice(0, 5).map(task => (
                      <div 
                        key={task.task_id}
                        className="flex items-center gap-3 py-2 px-3 text-gray-400"
                      >
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span className="text-sm line-through">{task.title}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="mentions">
              <div className="text-center py-12">
                <AtSign className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <h3 className="font-medium text-gray-700">No mentions</h3>
                <p className="text-sm text-gray-500 mt-1">
                  When someone mentions you in a comment or chat, it will appear here
                </p>
              </div>
            </TabsContent>

            <TabsContent value="notifications">
              <div className="text-center py-12">
                <Bell className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <h3 className="font-medium text-gray-700">No notifications</h3>
                <p className="text-sm text-gray-500 mt-1">
                  You&apos;re all caught up!
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </AppLayout>
  );
};

export default Inbox;
